import { useEffect, useState } from 'react';
import { payApiClient } from '@/api/client';

interface TestBenchAvailability {
  available: boolean;
  loading: boolean;
}

let cachedProbe: Promise<boolean> | undefined;

function probeAvailability(): Promise<boolean> {
  if (!cachedProbe) {
    cachedProbe = payApiClient
      .getTestBenchStatus()
      .then((status) => status.enabled)
      // Fail closed on any error, not only the documented 404/403: a
      // transient 500 here must hide the nav entry and preset button rather
      // than surface a half-broken feature.
      .catch(() => false);
  }
  return cachedProbe;
}

/** Test-only escape hatch: clears the module-level cache so each test starts from a fresh probe. */
export function resetTestBenchAvailabilityCache(): void {
  cachedProbe = undefined;
}

/**
 * Probes `GET /v1/admin/test-bench/status` exactly once per app session
 * (module-level cached promise), regardless of how many components mount
 * this hook. `available` stays `false` while `loading` is `true` so callers
 * never need to juggle a third "unknown" state.
 */
export function useTestBenchAvailability(): TestBenchAvailability {
  const [available, setAvailable] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    probeAvailability().then((result) => {
      if (cancelled) return;
      setAvailable(result);
      setLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, []);

  return { available, loading };
}
